import * as React from "react";
import { action, map, onMount, onSet } from "nanostores";
import { useStore, UseStoreOptions } from "@nanostores/react";
import { useNavigate } from "react-router-dom";
import { groupControllerFindAll } from "src/api/group";
import { Group } from "src/api/data-contracts";
import { CancellablePromise } from "@elsa-zku/lib/interfaces/api";
import arrayToIdIndex from "@elsa-zku/lib/helpers/arrayToIdIndex";

export interface GroupStore {
  list: number[],
  dict: Record<string, Group>,
  selected: number,
  loader: CancellablePromise<Group[]>,
  modeGroupFilter: number, // 0 - дома; 1 - группы (ЖК)
  isFilter: boolean, // true - применить фильтр по выбр.группе; false - игнорировать выбранную группу
}
export const groupStore = map<GroupStore>({
  list: null,
  dict: null,
  selected: null,
  loader: null,
  modeGroupFilter: 0,
  isFilter: false,
})
export type useGroupOptions = UseStoreOptions<typeof groupStore>;
export const useGroupStore = (options?: useGroupOptions) => useStore(groupStore, options);


const getValidSelected = (id: number, dict: GroupStore["dict"], list: GroupStore["list"]) => {
  return dict[id] ? id : list[0];
}

/* #region Сохранение/чтение идентификатора выбранной группы в store и localStorage */
export const selectGroup = action(groupStore, 'select', (store, id: number) => {
  const { dict, list } = store.get();
  id = getValidSelected(id, dict, list);
  store.setKey('selected', id);
  localStorage.setItem('group_id', String(id));
})

export const useSelectedGroupId = () => {
  const { selected } = useGroupStore();
  return selected;
}
/* #endregion */

export const loadGroups = action(groupStore, 'loadGroups', (store) => {
  const loader = groupControllerFindAll().then((groups) => {
    const dict = arrayToIdIndex(groups); //{"1":{"id":1,"name":"Первая группа","allowedBuildings":[],"allowedGates":[]}, "3":{"id":3,"name":"3гр","allowedBuildings":[{"id":3,"name":"Загородная ал. 2"}],"allowedGates":[{"id":2,"name":"супер панель"}]}}
    const list = groups.map(g => g.id);
    const selected = getValidSelected(+localStorage.getItem('group_id'), dict, list);
    const modeGroupFilter = +localStorage.getItem('modeGroupFilter');
    const isFilter = !!localStorage.getItem('isGroupFilter');
    store.set({
      dict,
      list,
      selected,
      loader,
      modeGroupFilter, // фильтр панелей по домовым (0) или групповым панелям (1)
      isFilter, // true - применить фильтр по выбр.группе; false - игнорировать выбранную группу (для панелей и домов)
    });
    localStorage.setItem('group_id', String(selected));
    if (modeGroupFilter) localStorage.setItem('modeGroupFilter', '1'); else localStorage.removeItem('modeGroupFilter');
    if (isFilter) localStorage.setItem('isGroupFilter', '1'); else localStorage.removeItem('isGroupFilter');
    return groups;
  }, (err: any) => {
    if (err.status !== 401) {
      alert(err);
    }
    throw err;
  });

  loader.catch(() => { })

  store.setKey('loader', loader);
});

// #region ФИЛЬТРОВАТЬ ПО ГРУППЕ
/** Установить режим фильтрации true - панели выбранной группы; false - все панели */
export const setIsGroupFilter = action(groupStore, 'select', (store, value: boolean) => {
  store.setKey('isFilter', value);
  if (value) localStorage.setItem('isGroupFilter', '1'); else localStorage.removeItem('isGroupFilter');
})

/** Вернуть выбранный режим фильтрации: true - по выбранной группе; false - все панели */
export const useIsGroupFilter = () => {
  const { isFilter } = useGroupStore();
  return isFilter;
};
// #endregion

const groupListConfig: useGroupOptions = { keys: ['list', 'dict'] }

/** Список ЖК */
export const useGroupsList = () => {
  const { list, dict } = useGroupStore(groupListConfig);
  if (!list) return [];
  return list.map(id => dict[id]);
}


//======= modeGroup ======
/** Фильтр по типу панели ЖК\подъездная. Варианты: label - для фильтра над таблицей, controlLabel - для формы создания-правки панелей */
export const modeGroupOptions = [
  { value: 0, label: 'Для подъездов', controlLabel: 'Для подъезда' },
  { value: 1, label: 'Для групп домов', controlLabel: 'Для группы домов' },
]

/** Вернуть выбранный режим подъезы/ЖК в виде идентификатора */
export const useModeGroupFilter = () => {
  const { modeGroupFilter } = useGroupStore();
  return modeGroupFilter;
};

/** Фильтр по типу панели ЖК\подъездная. Изменить состояние */
export const setModeGroupFilter = action(groupStore, 'select', (store, id: number) => {
  store.setKey('modeGroupFilter', id);
  if (id) localStorage.setItem('modeGroupFilter', String(id));
  else localStorage.removeItem('modeGroupFilter');
})
//======= /modeGroup ======


onMount(groupStore, () => {
  loadGroups();
});